import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

import { getErrorMessage } from '@/api/api-client'
import { useCreatePickupOrder } from '@/api/pickup-orders/queries/usePickupOrders'
import { AppTooltip } from '@/components/AppTooltip'
import { CopyTextButton } from '@/components/CopyTextButton'
import { Divider } from '@/components/Divider'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/useToast'
import { clarityEvent } from '@/lib/clarity'
import { cn } from '@/lib/utils'

import { PkgWarehouse } from '../../../../api/user-package/types/UserPkg'
import InfoIcon from '../../../../components/icons/info.svg?react'
import { WarehousePreview } from '../../../../components/WarehousePreview'
import { UploadArea } from '../../ReceiptUpload/components/UploadArea'
import primarkBarcodeImg from './primark-barcode-example.jpeg'
import { PrimarkExplainDialog } from './PrimarkExplainDialog'
import { WarehouseAddressPreview } from './WarehouseAddressPreview'

interface PrimarkFormProps {
  fullName: string
}

type PrimarkFormData = {
  pickupDate: string
  orderNumber: string
  barcodeFile: File | null
}

const defaultValues: PrimarkFormData = {
  pickupDate: '',
  orderNumber: '',
  barcodeFile: null,
}

export const PrimarkForm = ({ fullName }: PrimarkFormProps) => {
  const createMutation = useCreatePickupOrder()
  const { showSuccessToast, showErrorToast } = useToast()
  const [uploadAreaKey, setUploadAreaKey] = useState(0)
  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false)
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PrimarkFormData>({
    defaultValues,
    mode: 'onSubmit',
  })

  const { t } = useTranslation()

  useEffect(() => {
    clarityEvent('primark_form_viewed')
  }, [])

  const onSubmit = async (data: PrimarkFormData) => {
    try {
      await createMutation.mutateAsync({
        pickupDate: data.pickupDate,
        orderNumber: data.orderNumber.trim(),
        barcodeFile: data.barcodeFile,
      })

      clarityEvent('primark_order_created')
      reset(defaultValues)
      // Reset UploadArea by remounting it with a new key
      setUploadAreaKey(prev => prev + 1)
      showSuccessToast(t('primark.packageReceived'), t('primark.packageReceivedDesc'))
    } catch (error) {
      // Error is handled by the mutation's error state
      showErrorToast(t('primark.createOrderError'), getErrorMessage(error))
    }
  }
  const tomorrow = useMemo(() => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    return tomorrow
  }, [])
  return (
    <Card className='border border-[#d0d5dd] bg-white shadow-[0px_1px_3px_0px_rgba(0,0,0,0.08),0px_1px_2px_0px_rgba(0,0,0,0.05)]'>
      <CardContent className='p-6'>
        <div className=''>
          <div className='mb-2 flex items-center gap-2'>
            <WarehousePreview warehouse={PkgWarehouse.UK} />
            <h2 className='text-text-primary text-right leading-6 font-medium'>{t('primark.pickupDetailsTitle')}</h2>
          </div>
          <div className='mb-6 text-right'>
            <button
              type='button'
              onClick={() => setIsHowItWorksOpen(true)}
              className='text-text-link mt-1 inline-flex cursor-pointer items-center gap-1 text-sm underline hover:no-underline'
            >
              {t('primark.howItWorksLink')}
            </button>
          </div>
          <PrimarkExplainDialog isOpen={isHowItWorksOpen} onOpenChange={setIsHowItWorksOpen} />
          <form onSubmit={handleSubmit(onSubmit)}>
            <WarehouseAddressPreview index={0} isAddressLine2={false} field={{ label: 'Full Name', value: fullName }} />
            <Divider className='bg-border-default mt-4 mb-3' />
            <div className={cn('flex items-center justify-between')}>
              <div className='flex items-center gap-3'>
                <CopyTextButton value='Staines-upon-Thames' tooltipText={t('primark.copied')} />
              </div>
              <div className='flex flex-col items-end text-end'>
                <p className='text-text-secondary mb-1 text-sm leading-5'>Pickup Store</p>
                <p dir='ltr' className='text-text-primary text-left text-sm leading-5 font-medium whitespace-pre-line'>
                  Staines
                </p>
                <p className='text-text-primary text-xs leading-4'>Staines-upon-Thames, Elmsleigh Shopping Centre, 62 South Street</p>
              </div>
            </div>
            <Divider className='bg-border-default mt-4 mb-3' />
            <Input
              {...register('pickupDate', { required: t('primark.pickupDateRequired') })}
              labelProps={{ dir: 'ltr', className: 'mb-4' }}
              label='Pickup Date'
              id='pickup-date'
              type='date'
              min={tomorrow.toISOString().split('T')[0]}
              className='h-12 w-auto rounded-2xl border-[#e5e9f2]'
              placeholder='dd/MM/yyyy'
              aria-invalid={!!errors.pickupDate}
              helperText={errors.pickupDate?.message}
              error={!!errors.pickupDate}
            />
            <Divider className='bg-border-default mt-4 mb-3' />

            <Input
              {...register('orderNumber', {
                required: t('primark.orderNumberRequired'),
                validate: value => {
                  const trimmed = value?.trim() ?? ''
                  if (!/^GB\d{10}$/.test(trimmed)) {
                    return (t as (key: string) => string)('primark.orderNumberFormat')
                  }
                  return true
                },
              })}
              labelProps={{ dir: 'ltr', className: 'mb-4' }}
              label='Order Number'
              id='order-number'
              type='text'
              className='h-12 w-full rounded-2xl border-[#e5e9f2]'
              placeholder='GB1234567890'
              dir='ltr'
              aria-invalid={!!errors.orderNumber}
              helperText={errors.orderNumber?.message}
              error={!!errors.orderNumber}
            />
            <Divider className='bg-border-default mt-4 mb-3' />

            {/* Upload Barcode Field */}
            <div className='pb-4'>
              <div className='mb-4 flex w-full flex-col items-end'>
                <Label htmlFor='barcode-upload' className='text-text-primary text-left text-sm font-normal'>
                  Upload Barcode
                </Label>
                <p className='text-text-secondary mt-2 text-left text-xs leading-4'>{t('primark.barcodeUploadHelper')}</p>
              </div>
              <Controller
                name='barcodeFile'
                control={control}
                rules={{ required: t('primark.barcodeRequired') }}
                render={({ field }) => (
                  <UploadArea
                    helperText=''
                    helperImageUrl={primarkBarcodeImg}
                    maxFileCount={1}
                    key={uploadAreaKey}
                    onChangeFile={file => {
                      field.onChange(file)
                    }}
                  />
                )}
              />
              {errors.barcodeFile && <div className='text-destructive mt-2 text-right text-sm'>{errors.barcodeFile.message}</div>}
              <AppTooltip content={t('primark.barcodeTooltip')} contentProps={{ className: 'max-w-[200px]' }}>
                <div className='text-text-link mt-2 flex items-center gap-2 text-xs underline'>
                  <InfoIcon className='size-4' />
                  {t('primark.whatIsBarcodeLink')}
                </div>
              </AppTooltip>
            </div>
            <div className='flex flex-col items-center gap-2 pt-4'>
              <Button
                type='submit'
                loading={createMutation.isPending}
                disabled={createMutation.isPending}
                className='h-12 w-[335px] rounded-full bg-[#fad600] text-base font-medium text-[#333] hover:bg-[#fad600]/90'
              >
                {t('primark.submitButton')}
              </Button>
              <small className='text-text-secondary max-w-[335px] text-center text-xs'>
                <Trans i18nKey='primark.serviceDisclaimer' />
              </small>
            </div>
          </form>
        </div>
      </CardContent>
    </Card>
  )
}
